import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { AdService } from "@services/ads/AdService";
import "./coverPicker.css";

/*
    Pick the frame a network shows before the video plays (MPN-1761).

    Covers are derived automatically -- the last frame, where the CTA card is -- and that
    is right for almost every video and wrong for some. This is the correction, and it is
    what lets a rendered ad and a migrated ad take their covers from different rules
    without that mattering: either can be fixed here.

    The frame is captured in the browser. The media bucket allows cross-origin reads, so a
    crossOrigin video drawn into a canvas is not tainted and `toBlob` gives us the exact
    pixels on screen -- no second decode server-side, and no chance of the saved cover
    differing from the one the operator chose.
*/

interface CoverPickerProps {
    adId: number;
    /** The video to scrub, presigned. */
    videoUrl: string;
    adName?: string;
    /** The captured frame, as an object URL, so the card can show it without a refetch. */
    onSaved: (previewUrl: string) => void;
    onClose: () => void;
    /*
        Stacking, for a caller that opens this from inside another modal (MPN-1761).

        This is a react-bootstrap Modal, so it paints at Bootstrap's 1055 with its backdrop
        at 1050. The Create Editor is a fullScreen MUI Dialog at MUI's 1300, so opened from
        there the picker mounts UNDERNEATH an opaque dialog and the button reads as dead.
        The caller says where to put it; every nested modal in createEditor already passes
        the same `ceh-nested-*` pair.
    */
    className?: string;
    backdropClassName?: string;
    style?: CSSProperties;
}

const JPEG_QUALITY = 0.92;

export default function CoverPicker({
    adId, videoUrl, adName, onSaved, onClose, className, backdropClassName, style,
}: CoverPickerProps) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [duration, setDuration] = useState(0);
    const [position, setPosition] = useState(0);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // A seek is asynchronous. Capturing before `seeked` lands grabs whatever the element
    // last painted -- the previous frame, or black on first open -- and uploads it as the
    // published cover, silently. Save waits for the frame it is showing.
    const [seeking, setSeeking] = useState(true);
    const [failed, setFailed] = useState(false);

    // Open on the frame that is already the cover: the last one. Seeking exactly to
    // `duration` lands past the final frame on some files and paints nothing, so sit a
    // hair inside it.
    const onLoadedMetadata = useCallback(() => {
        const vid = videoRef.current;
        if (!vid || !Number.isFinite(vid.duration)) return;
        setDuration(vid.duration);
        const end = Math.max(vid.duration - 0.05, 0);
        setSeeking(true);
        vid.currentTime = end;
        setPosition(end);
    }, []);

    const scrub = useCallback((seconds: number) => {
        const vid = videoRef.current;
        if (!vid) return;
        setSeeking(true);
        vid.currentTime = seconds;
        setPosition(seconds);
    }, []);

    const save = useCallback(async () => {
        const vid = videoRef.current;
        if (!vid) return;
        setSaving(true);
        setError(null);
        try {
            const canvas = document.createElement("canvas");
            canvas.width = vid.videoWidth;
            canvas.height = vid.videoHeight;
            canvas.getContext("2d")?.drawImage(vid, 0, 0, canvas.width, canvas.height);

            const blob = await new Promise<Blob | null>((resolve) =>
                canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY)
            );
            if (!blob) throw new Error("This frame could not be captured.");

            await new AdService().setAdThumbnail(
                adId,
                new File([blob], "cover.jpg", { type: "image/jpeg" }),
                { width: canvas.width, height: canvas.height },
            );
            // The caller owns this URL and revokes it when it is replaced or unmounted.
            onSaved(URL.createObjectURL(blob));
        } catch (err) {
            // Includes a tainted canvas, if the bucket's CORS is ever tightened: the
            // operator sees why rather than a cover that silently never saves.
            setError(err instanceof Error ? err.message : "That cover could not be saved.");
        } finally {
            setSaving(false);
        }
    }, [adId, onSaved]);

    useEffect(() => {
        const vid = videoRef.current;
        if (vid && vid.readyState >= 1) onLoadedMetadata();
    }, [onLoadedMetadata]);

    return (
        <Modal
            show
            onHide={onClose}
            centered
            size="lg"
            className={className ? `cover-picker ${className}` : "cover-picker"}
            backdropClassName={backdropClassName}
            style={style}
        >
            <Modal.Header closeButton>
                <Modal.Title className="cover-picker__title">
                    Choose a cover{adName ? <span className="cover-picker__ad">{adName}</span> : null}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="cover-picker__stage">
                    <video
                        ref={videoRef}
                        src={videoUrl}
                        crossOrigin="anonymous"
                        preload="auto"
                        playsInline
                        muted
                        onLoadedMetadata={onLoadedMetadata}
                        onSeeked={() => setSeeking(false)}
                        onError={() => {
                            // An expired presigned URL or a blocked cross-origin read
                            // otherwise leaves a black stage and a dead Save button.
                            setFailed(true);
                            setSeeking(false);
                            setError("This video could not be loaded. Close and try again.");
                        }}
                    />
                </div>
                <input
                    className="cover-picker__scrub"
                    type="range"
                    min={0}
                    max={duration || 0}
                    step={0.04}
                    value={position}
                    disabled={!duration}
                    onChange={(e) => scrub(Number(e.target.value))}
                    aria-label="Scrub to a frame"
                />
                <div className="cover-picker__hint">
                    This is what the network shows before the video plays. It opens on the last
                    frame, where the call to action usually is.
                </div>
                {error && <div className="cover-picker__error">{error}</div>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="link" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="primary" onClick={save} disabled={saving || seeking || failed || !duration}>
                    {saving ? <><Spinner size="sm" animation="border" className="me-2" />Saving</> : "Use this frame"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
